import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Package, PackageGroupSize, AddOn } from '../../types/Package';
import { Category, loadCategoriesFromServer } from '../../utils/categories';
import CategoryQuickAddModal from './CategoryQuickAddModal';
import UploadWidget from '../Cloudinary/UploadWidget';
import { CloudinaryImage } from '../../types/Gallery';
import './PackageCreateWizard.css';

interface PackageCreateWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (packageData: Partial<Package>) => void;
  editingPackage?: Package | null;
}

interface WizardFormData {
  // Step 1: Basic Information
  title: string;
  shortDescription: string;
  fullDescription: string;
  
  // Step 2: Package Finder Attributes
  selectedCategoryIds: string[]; // Multiple gallery categories
  durationMinutes: number | '';
  groupSize: PackageGroupSize;
  
  // Step 3: Pricing & Currency
  price: number | '';
  currency: 'R' | 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';
  specialPrice: number | '';
  isSpecial: boolean;
  startAt: string;
  endAt: string;
  basePriceIncluded: string; // What's included in the base price
  
  // Step 4: Add-ons (optional)
  addons: AddOn[];
  
  // Step 5: Features & Display
  features: string[];
  badge: string;
  featured: boolean;
  available: boolean;
  order: number;
  
  // Step 6: Modal Customization
  showProcessSteps: boolean;
  showExpectations: boolean;
  showTestimonials: boolean;
  customBookingNote: string;
  selectedProcessStepIds: string[];
  selectedExpectationIds: string[];
  selectedTestimonialIds: string[];
  
  // Step 7: Images (optional)
  heroImage?: CloudinaryImage;
}

const TOTAL_STEPS = 7;

const PackageCreateWizard: React.FC<PackageCreateWizardProps> = ({
  isOpen,
  onClose,
  onSave,
  editingPackage
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<WizardFormData>({
    title: '',
    shortDescription: '',
    fullDescription: '',
    selectedCategoryIds: [],
    durationMinutes: '',
    groupSize: 'any',
    price: '',
    currency: 'R',
    specialPrice: '',
    isSpecial: false,
    startAt: '',
    endAt: '',
    basePriceIncluded: '',
    addons: [],
    features: [],
    badge: '',
    featured: false,
    available: true,
    order: 999,
    showProcessSteps: true,
    showExpectations: true,
    showTestimonials: true,
    customBookingNote: '',
    selectedProcessStepIds: [],
    selectedExpectationIds: [],
    selectedTestimonialIds: [],
  });

  const [galleryCategories, setGalleryCategories] = useState<Category[]>([]);
  const [globalAddons, setGlobalAddons] = useState<AddOn[]>([]);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [customizationOptions, setCustomizationOptions] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const wizardContentRef = useRef<HTMLDivElement>(null);

  // Initialize form data from editing package if provided
  useEffect(() => {
    if (isOpen && editingPackage) {
      // Convert category to slug(s) for selectedCategoryIds
      let categorySlugs: string[] = [];
      if (editingPackage.categories && editingPackage.categories.length > 0) {
        // Already have categories array - use it directly
        categorySlugs = editingPackage.categories;
      } else if (editingPackage.category) {
        // Have single category - need to convert name to slug
        const categoryName = editingPackage.category;
        // Try to find matching category by name or slug
        const matchedCategory = galleryCategories.find(c => 
          c.name.toLowerCase() === categoryName.toLowerCase() ||
          c.slug.toLowerCase() === categoryName.toLowerCase()
        );
        if (matchedCategory) {
          categorySlugs = [matchedCategory.slug];
        } else {
          // Generate slug from name if no match found
          const generatedSlug = categoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
          categorySlugs = [generatedSlug];
        }
      }
      
      setFormData({
        title: editingPackage.name || '',
        shortDescription: editingPackage.short || '',
        fullDescription: editingPackage.description || '',
        selectedCategoryIds: categorySlugs,
        durationMinutes: editingPackage.durationMinutes || '',
        groupSize: editingPackage.groupSize || 'any',
        price: editingPackage.price || '',
        currency: editingPackage.currency || 'R',
        specialPrice: editingPackage.specialPrice || '',
        isSpecial: editingPackage.isSpecial || false,
        startAt: editingPackage.startAt || '',
        endAt: editingPackage.endAt || '',
        basePriceIncluded: (editingPackage as any).basePriceIncluded || '',
        addons: editingPackage.addons || [],
        features: editingPackage.features || [],
        badge: editingPackage.badge || '',
        featured: editingPackage.featured || false,
        available: editingPackage.available !== false,
        order: editingPackage.order || 999,
        showProcessSteps: editingPackage.customization?.showProcessSteps !== false,
        showExpectations: editingPackage.customization?.showExpectations !== false,
        showTestimonials: editingPackage.customization?.showTestimonials !== false,
        customBookingNote: editingPackage.customization?.customBookingNote || '',
        selectedProcessStepIds: editingPackage.customization?.selectedProcessStepIds || [],
        selectedExpectationIds: editingPackage.customization?.selectedExpectationIds || [],
        selectedTestimonialIds: editingPackage.customization?.selectedTestimonialIds || [],
        heroImage: editingPackage.heroImage,
      });
      setCurrentStep(1); // Reset to first step when opening
    } else if (isOpen && !editingPackage) {
      // Reset to defaults for new package
      setFormData({
        title: '',
        shortDescription: '',
        fullDescription: '',
        selectedCategoryIds: [],
        durationMinutes: '',
        groupSize: 'any',
        price: '',
        currency: 'R',
        specialPrice: '',
        isSpecial: false,
        startAt: '',
        endAt: '',
        basePriceIncluded: '',
        addons: [],
        features: [],
        badge: '',
        featured: false,
        available: true,
        order: 999,
        showProcessSteps: true,
        showExpectations: true,
        showTestimonials: true,
        customBookingNote: '',
        selectedProcessStepIds: [],
        selectedExpectationIds: [],
        selectedTestimonialIds: [],
      });
      setCurrentStep(1);
    }
  }, [isOpen, editingPackage]);

  const durationPresets = [
    { id: '1hour', label: '1 Hour', description: '1 hour', minutes: 60 },
    { id: 'quick', label: 'Quick', description: 'Up to 2 hours', minutes: 120 },
    { id: 'standard', label: 'Standard', description: '>2 to 4 hours', minutes: 240 },
    { id: 'halfday', label: 'Half Day', description: '4 hours', minutes: 240 },
    { id: 'extended', label: 'Extended', description: '>4 to 6 hours', minutes: 360 },
    { id: 'fullday', label: 'Full Day', description: '8+ hours', minutes: 480 },
  ];

  const groupSizeOptions = [
    { id: 'solo', label: 'Solo / Couple (1-2 people)', description: '1-2 people', icon: 'fas fa-user' },
    { id: 'small', label: 'Small Group (3-6 people)', description: '3-6 people', icon: 'fas fa-users' },
    { id: 'medium', label: 'Medium Group (up to 12 people)', description: 'Up to 12 people', icon: 'fas fa-users' },
    { id: 'large', label: 'Large Group (up to 25 people)', description: 'Up to 25 people', icon: 'fas fa-users' },
    { id: 'verylarge', label: 'Very Large Group (>25 people)', description: 'Greater than 25 people', icon: 'fas fa-users' },
    { id: 'any', label: 'Any Size (flexible)', description: 'No preference', icon: 'fas fa-question-circle' },
  ];

  // Load gallery categories (needed for Step 2)
  useEffect(() => {
    if (isOpen && currentStep === 2) {
      loadGalleryCategories();
    }
  }, [isOpen, currentStep]);

  // Load customization options (needed for Step 7)
  useEffect(() => {
    if (isOpen && currentStep === 7) {
      loadCustomizationOptions();
    }
  }, [isOpen, currentStep]);

  // Load customization on mount
  useEffect(() => {
    if (isOpen) {
      loadCustomizationOptions();
    }
  }, [isOpen]);

  // Listen for category updates
  useEffect(() => {
    const handleCategoryUpdate = () => {
      if (currentStep === 2) {
        loadGalleryCategories();
      }
    };

    window.addEventListener('categories_updated', handleCategoryUpdate);
    window.addEventListener('storage', handleCategoryUpdate);

    return () => {
      window.removeEventListener('categories_updated', handleCategoryUpdate);
      window.removeEventListener('storage', handleCategoryUpdate);
    };
  }, [currentStep]);

  // Listen for customization updates
  useEffect(() => {
    const handleCustomizationUpdate = () => {
      loadCustomizationOptions();
    };

    window.addEventListener('service_customization_updated', handleCustomizationUpdate);
    window.addEventListener('focus', handleCustomizationUpdate);

    return () => {
      window.removeEventListener('service_customization_updated', handleCustomizationUpdate);
      window.removeEventListener('focus', handleCustomizationUpdate);
    };
  }, []);

  // Load addons on mount and listen for updates
  useEffect(() => {
    // Load addons on initial mount
    loadGlobalAddons();
    
    const handleAddonUpdate = () => {
      loadGlobalAddons();
    };
    
    window.addEventListener('addons_updated', handleAddonUpdate);
    window.addEventListener('storage', (e) => {
      if (e.key === 'admin_addons' || e.key === null) {
        loadGlobalAddons();
      }
    });
    
    return () => {
      window.removeEventListener('addons_updated', handleAddonUpdate);
    };
  }, []);

  // Prevent Escape key from closing wizard
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [isOpen]);

  // Scroll to top when step changes or wizard opens
  useEffect(() => {
    if (isOpen && wizardContentRef.current) {
      wizardContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, isOpen]);

  const loadGalleryCategories = async () => {
    try {
      const categories = await loadCategoriesFromServer();
      setGalleryCategories(categories);
    } catch (error) {
      console.error('Error loading gallery categories:', error);
      setGalleryCategories([]);
    }
    
    // Load global addons
    loadGlobalAddons();
  };

  const loadGlobalAddons = async () => {
    try {
      const { loadAddonsFromServer } = await import('../../utils/AddonAPI');
      const addons = await loadAddonsFromServer();
      console.log('📦 PackageCreateWizard: Loaded addons:', addons.length, addons.map(a => a.name));
      setGlobalAddons(addons);
    } catch (error) {
      console.error('Error loading addons:', error);
      setGlobalAddons([]);
    }
  };

  const loadCustomizationOptions = async () => {
    try {
      // Prefer server configuration
      const res = await fetch('/api/service-customization', { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (data && Object.keys(data).length > 0) {
          setCustomizationOptions(data);
          return;
        }
      }
    } catch (err) {
      console.warn('Failed to fetch customization from server, falling back to localStorage');
    }
    try {
      const raw = localStorage.getItem('package_customization') || localStorage.getItem('service_customization') || '{}';
      setCustomizationOptions(JSON.parse(raw));
    } catch (e) {
      setCustomizationOptions({});
    }
  };

  const getGlobalCustomization = (): any => customizationOptions || {};

  const handleCategoryAdded = (category: Category) => {
    // Auto-select the newly added category
    setFormData(prev => {
      const current = new Set(prev.selectedCategoryIds);
      current.add(category.slug);
      return {
        ...prev,
        selectedCategoryIds: Array.from(current)
      };
    });
    loadGalleryCategories();
  };

  const handleNext = () => {
    // Validate current step
    if (currentStep === 1) {
      if (!formData.title.trim() || !formData.shortDescription.trim() || !formData.fullDescription.trim()) {
        alert('Please fill in all required fields: Title, Short Description, and Full Description');
        return;
      }
    } else if (currentStep === 2) {
      if (formData.selectedCategoryIds.length === 0 || !formData.durationMinutes || formData.durationMinutes <= 0) {
        alert('Please select at least one category and enter a valid duration in minutes');
        return;
      }
    } else if (currentStep === 3) {
      if (!formData.price || formData.price <= 0) {
        alert('Please enter a valid base price');
        return;
      }
    }

    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const saveCurrentProgress = (skipValidation: boolean = false) => {
    // Skip validation if saving progress mid-form
    if (!skipValidation) {
      // Final validation
      if (!formData.title.trim() || !formData.shortDescription.trim() || !formData.fullDescription.trim()) {
        alert('Please fill in all required fields');
        return;
      }

      if (formData.selectedCategoryIds.length === 0 || !formData.durationMinutes || formData.durationMinutes <= 0) {
        alert('Please select at least one category and enter a valid duration');
        return;
      }

      if (!formData.price || formData.price <= 0) {
        alert('Please enter a valid base price');
        return;
      }
    }

    // Map form data to Package interface
    const toSlug = (val: string) => (val || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    const packageData: Partial<Package> = {
      id: editingPackage?.id,
      name: formData.title.trim(),
      slug: editingPackage?.slug || toSlug(formData.title.trim()),
      short: formData.shortDescription.trim(),
      description: formData.fullDescription.trim(),
      // Only set category name from first selected category for backward compatibility (not used for display)
      category: formData.selectedCategoryIds.length > 0 ? (() => {
        const firstSlug = formData.selectedCategoryIds[0];
        const firstCategory = galleryCategories.find(c => c.slug === firstSlug);
        return firstCategory?.name || firstSlug;
      })() : undefined,
      categories: formData.selectedCategoryIds.length > 0 ? formData.selectedCategoryIds : undefined, // Store all selected categories as slugs (primary)
      durationMinutes: Number(formData.durationMinutes),
      groupSize: formData.groupSize,
      price: Number(formData.price),
      currency: formData.currency,
      specialPrice: formData.specialPrice ? Number(formData.specialPrice) : undefined,
      isSpecial: formData.isSpecial,
      startAt: formData.startAt || undefined,
      endAt: formData.endAt || undefined,
      basePriceIncluded: formData.basePriceIncluded && formData.basePriceIncluded.trim() ? formData.basePriceIncluded.trim() : undefined,
      addons: formData.addons.length > 0 ? formData.addons : undefined,
      features: formData.features.length > 0 ? formData.features : undefined,
      badge: formData.badge || undefined,
      featured: formData.featured,
      available: formData.available,
      order: formData.order,
      customization: {
        showProcessSteps: formData.showProcessSteps,
        showExpectations: formData.showExpectations,
        showTestimonials: formData.showTestimonials,
        customBookingNote: formData.customBookingNote || undefined,
        selectedProcessStepIds: formData.selectedProcessStepIds.length > 0 ? formData.selectedProcessStepIds : undefined,
        selectedExpectationIds: formData.selectedExpectationIds.length > 0 ? formData.selectedExpectationIds : undefined,
        selectedTestimonialIds: formData.selectedTestimonialIds.length > 0 ? formData.selectedTestimonialIds : undefined,
      },
      heroImage: formData.heroImage,
      image: formData.heroImage?.secure_url,
    };

    onSave(packageData);
    if (!skipValidation) {
      handleClose();
    }
  };

  const handleCreate = () => {
    saveCurrentProgress(false);
  };

  const handleSaveProgress = () => {
    saveCurrentProgress(true);
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({
      title: '',
      shortDescription: '',
      fullDescription: '',
      selectedCategoryIds: [],
      durationMinutes: '',
      groupSize: 'any',
      price: '',
      currency: 'R',
      specialPrice: '',
      isSpecial: false,
      startAt: '',
      endAt: '',
      basePriceIncluded: '',
      addons: [],
      features: [],
      badge: '',
      featured: false,
      available: true,
      order: 999,
      showProcessSteps: true,
      showExpectations: true,
      showTestimonials: true,
      customBookingNote: '',
      selectedProcessStepIds: [],
      selectedExpectationIds: [],
      selectedTestimonialIds: [],
    });
    onClose();
  };


  const applyDurationPreset = (minutes: number) => {
    setFormData(prev => ({
      ...prev,
      durationMinutes: minutes
    }));
  };


  const addAddon = () => {
    setFormData(prev => ({
      ...prev,
      addons: [...prev.addons, { name: '', price: undefined }]
    }));
  };

  const removeAddon = (index: number) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.filter((_, i) => i !== index)
    }));
  };

  const updateAddon = (index: number, field: keyof AddOn, value: any) => {
    setFormData(prev => {
      const newAddons = [...prev.addons];
      newAddons[index] = { ...newAddons[index], [field]: value };
      return { ...prev, addons: newAddons };
    });
  };

  const handleImageUpload = (images: CloudinaryImage[]) => {
    if (images && images.length > 0) {
      setFormData(prev => ({
        ...prev,
        heroImage: images[0]
      }));
    }
  };

  // Convert formData to EditState format for preview
  const getPreviewPackage = (): Partial<Package> & { isNew?: boolean } => {
    return {
      isNew: !editingPackage,
      id: editingPackage?.id,
      name: formData.title,
      short: formData.shortDescription,
      description: formData.fullDescription,
      // Only set category name from first selected category for backward compatibility (not used for display)
      category: formData.selectedCategoryIds.length > 0 ? (() => {
        const firstSlug = formData.selectedCategoryIds[0];
        const firstCategory = galleryCategories.find(c => c.slug === firstSlug);
        return firstCategory?.name || firstSlug;
      })() : undefined,
      categories: formData.selectedCategoryIds.length > 0 ? formData.selectedCategoryIds : undefined, // Store all selected categories as slugs (primary)
      durationMinutes: typeof formData.durationMinutes === 'number' ? formData.durationMinutes : undefined,
      groupSize: formData.groupSize,
      price: typeof formData.price === 'number' ? formData.price : undefined,
      currency: formData.currency,
      specialPrice: typeof formData.specialPrice === 'number' ? formData.specialPrice : undefined,
      isSpecial: formData.isSpecial,
      startAt: formData.startAt || undefined,
      endAt: formData.endAt || undefined,
      addons: formData.addons,
      features: formData.features,
      badge: formData.badge || undefined,
      featured: formData.featured,
      available: formData.available,
      order: formData.order,
      image: formData.heroImage?.secure_url,
      heroImage: formData.heroImage,
      customization: {
        showProcessSteps: formData.showProcessSteps,
        showExpectations: formData.showExpectations,
        showTestimonials: formData.showTestimonials,
        customBookingNote: formData.customBookingNote,
        selectedProcessStepIds: formData.selectedProcessStepIds,
        selectedExpectationIds: formData.selectedExpectationIds,
        selectedTestimonialIds: formData.selectedTestimonialIds,
      }
    };
  };

  // PackagePreview component (inline)
  const PackagePreview: React.FC<{ package: Partial<Package> & { isNew?: boolean } }> = ({ package: packageItem }) => {
    const [previewCustomizationData, setPreviewCustomizationData] = useState<any>(null);

    useEffect(() => {
      if (packageItem.customization) {
        setPreviewCustomizationData(packageItem.customization);
      } else {
        const savedData = localStorage.getItem('package_customization');
        if (savedData) {
          try {
            setPreviewCustomizationData(JSON.parse(savedData));
          } catch (error) {
            console.error('Error loading package customization:', error);
          }
        }
      }
    }, [packageItem.customization]);

    const formatFeatureText = (text: string) => {
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>')
        .replace(/•/g, '<span class="bullet-point">•</span>');
    };

    const getProcessSteps = () => {
      if (previewCustomizationData?.processSteps) {
        return previewCustomizationData.processSteps.sort((a: any, b: any) => a.number - b.number);
      }
      return [
        { id: '1', number: 1, title: 'Initial Consultation', description: 'We\'ll discuss your vision, preferences, and specific requirements to tailor the perfect session for you.' },
        { id: '2', number: 2, title: 'Planning & Preparation', description: 'I\'ll provide guidance on wardrobe, location, timing, and any special considerations for your session.' },
        { id: '3', number: 3, title: 'Photography Session', description: 'Professional photography with natural posing guidance and a relaxed, enjoyable atmosphere.' },
        { id: '4', number: 4, title: 'Editing & Delivery', description: 'Careful editing of your images with professional retouching and delivery via online gallery.' }
      ];
    };

    const getExpectations = () => {
      if (previewCustomizationData?.expectations) {
        return previewCustomizationData.expectations;
      }
      return [
        { id: '1', icon: 'fas fa-camera', title: 'Professional Equipment', description: 'State-of-the-art camera gear and professional lighting equipment for stunning results.' },
        { id: '2', icon: 'fas fa-heart', title: 'Personal Touch', description: 'Every session is tailored to your unique style and personality for authentic, meaningful photos.' },
        { id: '3', icon: 'fas fa-clock', title: 'Flexible Timing', description: 'I work around your schedule to find the perfect time and lighting for your session.' },
        { id: '4', icon: 'fas fa-star', title: 'Quality Guarantee', description: 'Professional editing and retouching to ensure you receive only the highest quality images.' }
      ];
    };

    const getTestimonials = () => {
      if (previewCustomizationData?.testimonials) {
        return previewCustomizationData.testimonials;
      }
      return [
        { id: '1', content: 'Carmen has an incredible eye for detail and made our session so comfortable and fun. The photos exceeded our expectations!', author: 'Sarah M.', service: 'Portrait Package' },
        { id: '2', content: 'Professional, creative, and so easy to work with. Carmen captured our family perfectly and we\'ll treasure these photos forever.', author: 'John & Lisa', service: 'Family Package' }
      ];
    };

    const getBookingNote = () => {
      return previewCustomizationData?.customBookingNote || previewCustomizationData?.bookingNote || 'Free consultation included • Flexible scheduling • Professional results guaranteed';
    };

    const price = packageItem.isSpecial && packageItem.specialPrice ? packageItem.specialPrice : packageItem.price;
    const hasDiscount = Boolean(packageItem.isSpecial && packageItem.specialPrice && packageItem.price && packageItem.specialPrice < packageItem.price);

    return (
      <div className="package-preview">
        <div className="preview-header">
          <h3>Live Preview</h3>
          <p>How your package will appear to customers</p>
        </div>
        
        <div className="preview-content">
          {/* Header */}
          <div className="preview-section">
            <div className="preview-package-header">
              <div className="preview-badge-section">
                {packageItem.badge && (
                  <div className={`package-badge ${packageItem.badge.toLowerCase()}`}>
                    <span>{packageItem.badge}</span>
                  </div>
                )}
                <h4 className="preview-package-title">{packageItem.name || 'Package Name'}</h4>
              </div>
              <div className="preview-package-meta">
                {packageItem.durationMinutes && (
                  <div className="duration-info">
                    <i className="fas fa-clock"></i>
                    <span>{packageItem.durationMinutes} minutes</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Hero Image */}
          {packageItem.image && (
            <div className="preview-section">
              <div className="preview-hero-image">
                <img src={packageItem.image} alt={packageItem.name || 'Package'} />
                <div className="preview-image-overlay">
                  <div className="preview-price-display">
                    {hasDiscount && (
                      <span className="preview-original-price">
                        {packageItem.currency || 'R'} {packageItem.price?.toFixed(0)}
                      </span>
                    )}
                    <span className="preview-current-price">
                      {packageItem.currency || 'R'} {price?.toFixed(0)}
                      {packageItem.isSpecial && <span className="preview-special-badge">Special</span>}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="preview-content-grid">
            {/* Main Content */}
            <div className="preview-main-content">
              {/* Description */}
              {packageItem.short && (
                <div className="preview-content-section">
                  <h5>Overview</h5>
                  <p className="preview-package-overview">{packageItem.short}</p>
                </div>
              )}

              {packageItem.description && (
                <div className="preview-content-section">
                  <h5>What's Included</h5>
                  <p className="preview-package-description">{packageItem.description}</p>
                </div>
              )}

              {/* Features */}
              {packageItem.features && packageItem.features.length > 0 && (
                <div className="preview-content-section">
                  <h5>Features & Benefits</h5>
                  <ul className="preview-features-list">
                    {packageItem.features.map((feature, index) => (
                      <li key={index}>
                        <i className="fas fa-check-circle"></i>
                        <div className="preview-feature-content" dangerouslySetInnerHTML={{ __html: formatFeatureText(feature) }} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Process Steps */}
              {previewCustomizationData?.showProcessSteps !== false && (
                <div className="preview-content-section">
                  <h5>Process</h5>
                  <div className="preview-process-steps">
                    {getProcessSteps().slice(0, 2).map((step: any, index: number) => (
                      <div key={step.id} className="preview-process-step">
                        <div className="preview-step-number">{index + 1}</div>
                        <div className="preview-step-content">
                          <h6>{step.title}</h6>
                          <p>{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expectations */}
              {previewCustomizationData?.showExpectations !== false && (
                <div className="preview-content-section">
                  <h5>What to Expect</h5>
                  <div className="preview-expectation-grid">
                    {getExpectations().slice(0, 2).map((expectation: any) => (
                      <div key={expectation.id} className="preview-expectation-item">
                        <i className={expectation.icon}></i>
                        <h6>{expectation.title}</h6>
                        <p>{expectation.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="preview-sidebar">
              {/* Testimonials */}
              {previewCustomizationData?.showTestimonials !== false && (
                <div className="preview-testimonials-card">
                  <h5>What Clients Say</h5>
                  {getTestimonials().slice(0, 1).map((testimonial: any) => (
                    <div key={testimonial.id} className="preview-testimonial-item">
                      <div className="preview-testimonial-content">
                        <p>"{testimonial.content}"</p>
                      </div>
                      <div className="preview-testimonial-author">
                        <strong>{testimonial.author}</strong>
                        <span>{testimonial.service}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Call to Action */}
              <div className="preview-cta-section">
                <div className="preview-booking-note">
                  <i className="fas fa-info-circle"></i>
                  <span>{getBookingNote()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const stepLabels = [
    'Basic Info',
    'Package Finder',
    'Pricing',
    'Add-ons',
    'Features',
    'Modal Custom',
    'Images'
  ];

  return createPortal(
    <div className="package-create-wizard-overlay">
      <div className="package-create-wizard" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="wizard-header">
          <div className="wizard-header-content">
            <h2>{editingPackage ? 'Edit Package' : 'Create New Package'}</h2>
            {editingPackage && editingPackage.name && (
              <div className="package-name-header-display" style={{
                marginTop: '0.75rem',
                padding: '0.75rem 1rem',
                background: '#FFFFFF',
                border: '2px solid #8B4513',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                boxShadow: '0 2px 8px rgba(139, 69, 19, 0.1)'
              }}>
                <span style={{
                  color: '#6C757D',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Package:</span>
                <span style={{
                  color: '#8B4513',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  fontFamily: 'Inter, sans-serif'
                }}>{editingPackage.name}</span>
              </div>
            )}
          </div>
          <div className="wizard-header-actions">
            <button 
              className={`preview-toggle-btn ${showPreview ? 'active' : ''}`}
              onClick={() => setShowPreview(!showPreview)}
              title={showPreview ? 'Hide Preview' : 'Show Preview'}
            >
              <i className={`fas ${showPreview ? 'fa-edit' : 'fa-eye'}`}></i>
              <span>{showPreview ? 'Edit' : 'Preview'}</span>
            </button>
            <button className="wizard-close-btn" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        {!showPreview && (
          <div className="wizard-progress">
            <div className="progress-steps">
              {stepLabels.map((label, index) => {
                const stepNumber = index + 1;
                const isCompleted = currentStep > stepNumber;
                const isActive = currentStep === stepNumber;
                const isClickable = true; // Allow clicking to any step
                
                return (
                  <div 
                    key={stepNumber}
                    className={`progress-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isClickable ? 'clickable' : ''}`}
                    onClick={() => {
                      if (isClickable) {
                        setCurrentStep(stepNumber);
                      }
                    }}
                    title={`Go to step ${stepNumber}: ${label}`}
                  >
                    <div className="step-number">{stepNumber}</div>
                    <div className="step-label">{label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="wizard-content" ref={wizardContentRef}>
          {showPreview ? (
            <PackagePreview package={getPreviewPackage()} />
          ) : (
            <>
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="wizard-step">
              <h3>Basic Information</h3>
              <p className="step-description">Enter the basic details for your package</p>
              
              <div className="form-group">
                <label htmlFor="package-title">Title *</label>
                <input
                  type="text"
                  id="package-title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Wedding Photography Package"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="package-short">Short Description *</label>
                <textarea
                  id="package-short"
                  value={formData.shortDescription}
                  onChange={(e) => setFormData({...formData, shortDescription: e.target.value})}
                  placeholder="A brief 1-2 sentence description"
                  rows={3}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="package-description">Full Description *</label>
                <textarea
                  id="package-description"
                  value={formData.fullDescription}
                  onChange={(e) => setFormData({...formData, fullDescription: e.target.value})}
                  placeholder="Detailed description of the package"
                  rows={6}
                  required
                />
              </div>
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  onClick={handleSaveProgress} 
                  className="btn btn-primary"
                  style={{ minWidth: '120px' }}
                >
                  <i className="fas fa-save"></i>
                  Save Progress
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Package Finder Attributes */}
          {currentStep === 2 && (
            <div className="wizard-step">
              <h3>Package Finder Attributes</h3>
              <p className="step-description">Configure how this package appears in the package finder</p>
              
              <div className="form-group">
                <label htmlFor="package-categories">Gallery Categories *</label>
                <small className="field-hint" style={{ display: 'block', marginBottom: '12px', color: '#6b7280' }}>
                  Select one or more gallery categories for this package
                </small>
                
                {galleryCategories.length === 0 ? (
                  <div style={{ marginTop: '8px' }}>
                    <small className="field-hint" style={{ color: '#ef4444', display: 'block', marginBottom: '8px' }}>
                      No categories available. Create one to get started!
                    </small>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowQuickAddModal(true)}
                      style={{ fontSize: '0.875rem', padding: '6px 12px' }}
                    >
                      <i className="fas fa-plus"></i>
                      Add New Category
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Selected Categories Display */}
                    {formData.selectedCategoryIds.length > 0 && (
                      <div className="multi-select-container" style={{ marginBottom: '12px' }}>
                        <div className="multi-select-dropdown" style={{ minHeight: '60px', maxHeight: '150px', overflowY: 'auto', padding: '8px' }}>
                          {formData.selectedCategoryIds.map(categorySlug => {
                            const category = galleryCategories.find(c => c.slug === categorySlug);
                            if (!category) return null;
                            return (
                              <span key={categorySlug} className="multi-select-chip" style={{ 
                                backgroundColor: category.color || '#8B4513',
                                color: 'white',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                margin: '4px'
                              }}>
                                {category.name}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const current = new Set(formData.selectedCategoryIds);
                                    current.delete(categorySlug);
                                    setFormData({...formData, selectedCategoryIds: Array.from(current)});
                                  }}
                                  style={{
                                    background: 'rgba(255, 255, 255, 0.3)',
                                    border: 'none',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    padding: 0
                                  }}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Category Selection Grid */}
                    <div style={{ 
                      maxHeight: '300px', 
                      overflowY: 'auto', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px', 
                      padding: '12px',
                      backgroundColor: '#f9fafb'
                    }}>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                        gap: '8px' 
                      }}>
                        {galleryCategories.map(category => {
                          const isSelected = formData.selectedCategoryIds.includes(category.slug);
                          return (
                            <label
                              key={category.id}
                              onClick={() => {
                                const current = new Set(formData.selectedCategoryIds);
                                if (isSelected) {
                                  current.delete(category.slug);
                                } else {
                                  current.add(category.slug);
                                }
                                setFormData({...formData, selectedCategoryIds: Array.from(current)});
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderRadius: '6px',
                                backgroundColor: isSelected ? (category.color || '#8B4513') : 'white',
                                color: isSelected ? 'white' : '#374151',
                                border: `2px solid ${isSelected ? (category.color || '#8B4513') : '#e5e7eb'}`,
                                transition: 'all 0.2s ease',
                                fontSize: '0.875rem',
                                fontWeight: isSelected ? 500 : 400
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                                  e.currentTarget.style.borderColor = category.color || '#8B4513';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = 'white';
                                  e.currentTarget.style.borderColor = '#e5e7eb';
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}} // Handled by label onClick
                                style={{ margin: 0, cursor: 'pointer' }}
                              />
                              <span style={{ flex: 1 }}>{category.name}</span>
                              {isSelected && (
                                <i className="fas fa-check" style={{ fontSize: '0.75rem' }}></i>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Add New Category Button */}
                    <div style={{ marginTop: '12px' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowQuickAddModal(true)}
                        style={{ fontSize: '0.875rem', padding: '6px 12px' }}
                      >
                        <i className="fas fa-plus"></i>
                        Add New Category
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="package-duration">Duration (minutes) *</label>
                <div className="duration-input-group">
                  <input
                    type="number"
                    id="package-duration"
                    value={formData.durationMinutes}
                    onChange={(e) => setFormData({...formData, durationMinutes: e.target.value ? Number(e.target.value) : ''})}
                    placeholder="Enter duration in minutes"
                    min="1"
                    required
                  />
                  <div className="duration-presets">
                    <span className="presets-label">Quick presets:</span>
                    {durationPresets.map(preset => (
                      <button
                        key={preset.id}
                        type="button"
                        className="preset-btn"
                        onClick={() => applyDurationPreset(preset.minutes)}
                      >
                        {preset.label} ({preset.minutes} min)
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="package-group-size">Group Size *</label>
                <select
                  id="package-group-size"
                  value={formData.groupSize}
                  onChange={(e) => setFormData({...formData, groupSize: e.target.value as PackageGroupSize})}
                  required
                >
                  {groupSizeOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  onClick={handleSaveProgress} 
                  className="btn btn-primary"
                  style={{ minWidth: '120px' }}
                >
                  <i className="fas fa-save"></i>
                  Save Progress
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Pricing & Currency */}
          {currentStep === 3 && (
            <div className="wizard-step">
              <h3>Pricing & Currency</h3>
              <p className="step-description">Set your package pricing and currency</p>
              
              <div className="form-group">
                <label htmlFor="package-price">Base Price *</label>
                <input
                  type="number"
                  id="package-price"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value ? Number(e.target.value) : ''})}
                  placeholder="Enter base price"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="package-currency">Currency *</label>
                <select
                  id="package-currency"
                  value={formData.currency}
                  onChange={(e) => setFormData({...formData, currency: e.target.value as any})}
                  required
                >
                  <option value="R">R (South African Rand)</option>
                  <option value="USD">USD (US Dollar)</option>
                  <option value="EUR">EUR (Euro)</option>
                  <option value="GBP">GBP (British Pound)</option>
                  <option value="CAD">CAD (Canadian Dollar)</option>
                  <option value="AUD">AUD (Australian Dollar)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="package-special-price">Special Price (Optional)</label>
                <input
                  type="number"
                  id="package-special-price"
                  value={formData.specialPrice}
                  onChange={(e) => setFormData({...formData, specialPrice: e.target.value ? Number(e.target.value) : ''})}
                  placeholder="Enter special offer price"
                  min="0"
                />
                <small className="field-hint">Leave empty for regular pricing</small>
              </div>

              <div className="form-group">
                <label htmlFor="base-price-included">What's Included in Base Price *</label>
                <textarea
                  id="base-price-included"
                  value={formData.basePriceIncluded}
                  onChange={(e) => setFormData({...formData, basePriceIncluded: e.target.value})}
                  placeholder="e.g., 2-hour session, 50 edited photos, online gallery, print release"
                  rows={4}
                  required
                />
                <small className="field-hint">Describe what customers get with the base price</small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isSpecial}
                    onChange={(e) => setFormData({...formData, isSpecial: e.target.checked})}
                  />
                  <span>Mark as Special Offer</span>
                </label>
              </div>

              {formData.isSpecial && (
                <>
                  <div className="form-group">
                    <label htmlFor="package-start-date">Special Start Date (Optional)</label>
                    <input
                      type="datetime-local"
                      id="package-start-date"
                      value={formData.startAt ? new Date(formData.startAt).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setFormData({...formData, startAt: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                    />
                    <small className="field-hint">When the special offer begins</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="package-end-date">Special End Date (Optional)</label>
                    <input
                      type="datetime-local"
                      id="package-end-date"
                      value={formData.endAt ? new Date(formData.endAt).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setFormData({...formData, endAt: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                    />
                    <small className="field-hint">When the special offer ends</small>
                  </div>
                </>
              )}
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  onClick={handleSaveProgress} 
                  className="btn btn-primary"
                  style={{ minWidth: '120px' }}
                >
                  <i className="fas fa-save"></i>
                  Save Progress
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Add-ons */}
          {currentStep === 4 && (
            <div className="wizard-step">
              <h3>Add-ons (Optional)</h3>
              <p className="step-description">Select from global addons or create custom addons for this package</p>
              
              {/* Global Addons Section */}
              <div className="form-group">
                <label>Global Addons {globalAddons.length > 0 ? `(${globalAddons.length} available)` : '(No addons configured)'}</label>
                {globalAddons.length > 0 ? (
                  <div className="global-addons-list" style={{ marginBottom: '20px' }}>
                    {globalAddons.map((addon) => {
                      const isSelected = formData.addons.some(a => a.id === addon.id);
                      return (
                        <div key={addon.id || addon.name} className={`global-addon-item ${isSelected ? 'selected' : ''}`} style={{ 
                          padding: '12px', 
                          marginBottom: '8px', 
                          border: `1px solid ${isSelected ? '#4CAF50' : '#ddd'}`, 
                          borderRadius: '4px',
                          backgroundColor: isSelected ? '#f0f9f0' : '#fff'
                        }}>
                          <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Add global addon
                                  setFormData({
                                    ...formData,
                                    addons: [...formData.addons, { ...addon }]
                                  });
                                } else {
                                  // Remove global addon
                                  setFormData({
                                    ...formData,
                                    addons: formData.addons.filter(a => a.id !== addon.id)
                                  });
                                }
                              }}
                              style={{ marginRight: '12px' }}
                            />
                            <div className="addon-info" style={{ flex: 1 }}>
                              <strong>{addon.name}</strong>
                              {addon.description && (
                                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                  {addon.description}
                                </div>
                              )}
                              <div style={{ fontSize: '0.9em', color: '#4CAF50', marginTop: '4px', fontWeight: 'bold' }}>
                                {addon.showFromPrice ? 'from ' : ''}{addon.currency || 'R'} {addon.price?.toFixed(0) || '0'}
                                {addon.pricingType === 'per-hour' && ' / hour'}
                                {addon.pricingType === 'per-each' && ' / each'}
                              </div>
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: '12px', marginBottom: '20px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9', color: '#666' }}>
                    <p style={{ margin: 0 }}>No global addons available. Create addons in <strong>Package add-ons</strong> first.</p>
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label>Custom Addons</label>
                <button type="button" className="btn btn-secondary" onClick={addAddon}>
                  <i className="fas fa-plus"></i>
                  Add Custom Service
                </button>
              </div>

              {formData.addons.map((addon, index) => (
                <div key={index} className="addon-item">
                  <div className="addon-header">
                    <h4>Add-on {index + 1}</h4>
                    <button type="button" className="btn-remove" onClick={() => removeAddon(index)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  <div className="form-group">
                    <label>Service Name</label>
                    <input
                      type="text"
                      value={addon.name}
                      onChange={(e) => updateAddon(index, 'name', e.target.value)}
                      placeholder="e.g. Extra Hour, Additional Locations"
                    />
                  </div>
                  <div className="form-group">
                    <label>Price (Optional)</label>
                    <input
                      type="number"
                      value={addon.price || ''}
                      onChange={(e) => updateAddon(index, 'price', e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="Add-on price"
                      min="0"
                    />
                    <small className="field-hint">Leave empty for free add-on</small>
                  </div>
                </div>
              ))}

              {formData.addons.length === 0 && (
                <div className="empty-state">
                  <p>No add-ons added. You can skip this step if you don't offer additional services.</p>
                </div>
              )}
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  onClick={handleSaveProgress} 
                  className="btn btn-primary"
                  style={{ minWidth: '120px' }}
                >
                  <i className="fas fa-save"></i>
                  Save Progress
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Features & Display */}
          {currentStep === 5 && (
            <div className="wizard-step">
              <h3>Features & Display Settings</h3>
              <p className="step-description">Key highlights and display options</p>
              
              <div className="form-group">
                <label htmlFor="package-features">Package Features</label>
                <textarea
                  id="package-features"
                  value={formData.features.join('\n')}
                  onChange={(e) => setFormData({...formData, features: e.target.value.split('\n').filter(f => f.trim())})}
                  placeholder="Enter features separated by new lines&#10;• Professional editing&#10;• High-resolution images&#10;• Online gallery"
                  rows={6}
                />
                <small className="field-hint">One feature per line. Use bullet points or simple text.</small>
              </div>

              <div className="form-group">
                <label htmlFor="package-badge">Badge</label>
                <select
                  id="package-badge"
                  value={formData.badge}
                  onChange={(e) => setFormData({...formData, badge: e.target.value})}
                >
                  <option value="">No Badge</option>
                  <option value="Popular">Popular</option>
                  <option value="New">New</option>
                  <option value="Special">Special</option>
                  <option value="Limited">Limited</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="package-order">Display Order</label>
                <input
                  type="number"
                  id="package-order"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: Number(e.target.value)})}
                  min="0"
                />
                <small className="field-hint">Lower numbers appear first</small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                  />
                  <span>Featured (Show on homepage)</span>
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.available}
                    onChange={(e) => setFormData({...formData, available: e.target.checked})}
                  />
                  <span>Available (Accepting bookings)</span>
                </label>
              </div>
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  onClick={handleSaveProgress} 
                  className="btn btn-primary"
                  style={{ minWidth: '120px' }}
                >
                  <i className="fas fa-save"></i>
                  Save Progress
                </button>
              </div>
            </div>
          )}

          {/* Step 6: Modal Customization */}
          {currentStep === 6 && (
            <div className="wizard-step">
              <h3>Modal Customization</h3>
              <p className="step-description">Choose which elements appear in this package's modal</p>
              
              <div className="form-group">
                <h4 style={{ marginBottom: '16px', fontSize: '1rem', fontWeight: 600 }}>Modal Components</h4>
                <div className="checkbox-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.showProcessSteps}
                      onChange={(e) => setFormData({...formData, showProcessSteps: e.target.checked})}
                    />
                    <div>
                      <div style={{ fontWeight: 500 }}>Show Process Steps</div>
                      <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>Display "Process" section</small>
                    </div>
                  </label>
                  
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.showExpectations}
                      onChange={(e) => setFormData({...formData, showExpectations: e.target.checked})}
                    />
                    <div>
                      <div style={{ fontWeight: 500 }}>Show Expectations</div>
                      <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>Display "What to Expect" section</small>
                    </div>
                  </label>
                  
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.showTestimonials}
                      onChange={(e) => setFormData({...formData, showTestimonials: e.target.checked})}
                    />
                    <div>
                      <div style={{ fontWeight: 500 }}>Show Testimonials</div>
                      <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>Display "What Clients Say" section</small>
                    </div>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="custom-booking-note">Custom Booking Note</label>
                <textarea
                  id="custom-booking-note"
                  value={formData.customBookingNote}
                  onChange={(e) => setFormData({...formData, customBookingNote: e.target.value})}
                  placeholder="Leave empty to use global booking note..."
                  rows={3}
                />
                <small className="field-hint">Override the default booking note for this package only</small>
              </div>

              {/* Selection of global items for this package */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginTop: '24px' }}>
                <div>
                  <h4 style={{ marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600 }}>Pick Process Steps</h4>
                  <small style={{ color: '#6b7280', display: 'block', marginBottom: '12px' }}>Select none to include all</small>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px' }}>
                    {(getGlobalCustomization().processSteps || []).length === 0 ? (
                      <div className="field-hint" style={{ padding: '12px', fontSize: '0.75rem' }}>
                        No process steps configured yet. Configure them in Admin → Package text.
                      </div>
                    ) : (
                      (getGlobalCustomization().processSteps || []).map((step: any) => {
                        const selected = formData.selectedProcessStepIds.includes(step.id);
                        return (
                          <label key={step.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', cursor: 'pointer', borderRadius: '4px', backgroundColor: selected ? '#eff6ff' : 'transparent' }}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => {
                                const current = new Set(formData.selectedProcessStepIds);
                                if (e.target.checked) current.add(step.id); else current.delete(step.id);
                                setFormData({...formData, selectedProcessStepIds: Array.from(current)});
                              }}
                            />
                            <span style={{ fontSize: '0.875rem' }}>{step.title} <small style={{ color: '#6b7280' }}>(#{step.number})</small></span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                <div>
                  <h4 style={{ marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600 }}>Pick What to Expect</h4>
                  <small style={{ color: '#6b7280', display: 'block', marginBottom: '12px' }}>Select none to include all</small>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px' }}>
                    {(getGlobalCustomization().expectations || []).length === 0 ? (
                      <div className="field-hint" style={{ padding: '12px', fontSize: '0.75rem' }}>
                        No expectations configured yet. Configure them in Admin → Package text.
                      </div>
                    ) : (
                      (getGlobalCustomization().expectations || []).map((item: any) => {
                        const selected = formData.selectedExpectationIds.includes(item.id);
                        return (
                          <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', cursor: 'pointer', borderRadius: '4px', backgroundColor: selected ? '#eff6ff' : 'transparent' }}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => {
                                const current = new Set(formData.selectedExpectationIds);
                                if (e.target.checked) current.add(item.id); else current.delete(item.id);
                                setFormData({...formData, selectedExpectationIds: Array.from(current)});
                              }}
                            />
                            <span style={{ fontSize: '0.875rem' }}>
                              <i className={item.icon} style={{ marginRight: '8px' }}></i>
                              {item.title}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                <div>
                  <h4 style={{ marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600 }}>Pick Testimonials</h4>
                  <small style={{ color: '#6b7280', display: 'block', marginBottom: '12px' }}>Select none to include all</small>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px' }}>
                    {(getGlobalCustomization().testimonials || []).length === 0 ? (
                      <div className="field-hint" style={{ padding: '12px', fontSize: '0.75rem' }}>
                        No testimonials configured yet. Configure them in Admin → Package text.
                      </div>
                    ) : (
                      (getGlobalCustomization().testimonials || []).map((t: any) => {
                        const selected = formData.selectedTestimonialIds.includes(t.id);
                        return (
                          <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', cursor: 'pointer', borderRadius: '4px', backgroundColor: selected ? '#eff6ff' : 'transparent' }}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => {
                                const current = new Set(formData.selectedTestimonialIds);
                                if (e.target.checked) current.add(t.id); else current.delete(t.id);
                                setFormData({...formData, selectedTestimonialIds: Array.from(current)});
                              }}
                            />
                            <span style={{ fontSize: '0.875rem' }}>{t.author} — {t.service}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  onClick={handleSaveProgress} 
                  className="btn btn-primary"
                  style={{ minWidth: '120px' }}
                >
                  <i className="fas fa-save"></i>
                  Save Progress
                </button>
              </div>
            </div>
          )}

          {/* Step 7: Images */}
          {currentStep === 7 && (
            <div className="wizard-step">
              <h3>Package Image (Optional)</h3>
              <p className="step-description">Add a single hero image for your package. You can add more images later when editing the package.</p>
              
              <div className="form-group">
                <UploadWidget
                  onUpload={handleImageUpload}
                  buttonText="Upload Hero Image"
                  folder="carmen_lorraine_photos/packages"
                />
              </div>

              {formData.heroImage && (
                <div className="image-preview">
                  <img 
                    src={formData.heroImage.secure_url} 
                    alt="Package hero"
                    className="preview-image"
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setFormData({...formData, heroImage: undefined})}
                  >
                    <i className="fas fa-times"></i>
                    Remove Image
                  </button>
                </div>
              )}

              <div className="empty-state">
                <p>You can skip this step and add images later when editing the package.</p>
              </div>
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  onClick={handleSaveProgress} 
                  className="btn btn-primary"
                  style={{ minWidth: '120px' }}
                >
                  <i className="fas fa-save"></i>
                  Save Progress
                </button>
              </div>
            </div>
          )}
            </>
          )}
        </div>

        {/* Footer Actions */}
        {!showPreview && (
          <div className="wizard-footer">
            <div className="wizard-actions">
              {currentStep > 1 && (
                <button className="btn btn-secondary" onClick={handleBack}>
                  <i className="fas fa-arrow-left"></i>
                  Back
                </button>
              )}
              <button className="btn btn-secondary" onClick={handleClose}>
                Cancel
              </button>
              {currentStep < TOTAL_STEPS ? (
                <button className="btn btn-primary" onClick={handleNext}>
                  Next
                  <i className="fas fa-arrow-right"></i>
                </button>
              ) : (
                <button className="btn btn-primary" onClick={handleCreate}>
                  <i className="fas fa-check"></i>
                  {editingPackage ? 'Save Changes' : 'Create Package'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Add Category Modal */}
      <CategoryQuickAddModal
        isOpen={showQuickAddModal}
        onClose={() => setShowQuickAddModal(false)}
        onCategoryAdded={handleCategoryAdded}
        existingCategories={galleryCategories}
      />
    </div>,
    document.body
  );
};

export default PackageCreateWizard;
